import React from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import { useSalonPublicTheme } from '../../../../context/SalonPublicThemeContext';
import SalonPublicButton from '../../salonPublicAppearance/SalonPublicButton';
import SalonPublicStyleScope from '../../salonPublicAppearance/SalonPublicStyleScope';


const TEAM_VARIANTS = new Set(['cards', 'creators', 'practitioners', 'club', 'specialists']);

const SalonTemplateTeam = ({
  data = [],
  variant = 'cards',
  visible = true,
  mode = 'public',
  onSelectMember,
}) => {
  const { t } = useTranslation();
  const { tokens: theme } = useSalonPublicTheme();
  const members = Array.isArray(data) ? data.filter((member) => member?.id != null) : [];
  const teamVariant = TEAM_VARIANTS.has(variant) ? variant : 'cards';
  const preview = mode === 'preview';

  if (!visible || members.length === 0) return null;

  return (
    <SalonPublicStyleScope
      sectionKey="team"
      data-testid="salon-section-team"
      data-salon-template-block="team"
      data-variant={teamVariant}
      sx={{ mt: 4 }}
    >
      <Box sx={{ mb: 3 }}>
        <Typography
          component="h2"
          data-salon-text-role="title"
          variant="h5"
          sx={{ color: theme.colors.gray[700], fontWeight: 600, mb: 1, fontSize: '1.5rem', letterSpacing: '0.025em' }}
        >
          {t('landing_salon_details.our_team')}
        </Typography>
        <Box sx={{ width: 60, height: 3, bgcolor: theme.colors.secondary[400], borderRadius: 2 }} />
      </Box>
      <Grid container spacing={{ xs: 1, md: 3 }} data-salon-text-role="content">
        {members.map((member) => {
          const initials = `${member.firstName?.[0] || '?'}${member.lastName?.[0] || ''}`.toUpperCase();
          return (
            <Grid key={member.id} item xs={6} sm={6} md={6} lg={4}>
              <Box
                component="article"
                sx={{
                  backgroundColor: theme.colors.background.primary,
                  borderRadius: 3,
                  border: `1px solid ${theme.colors.border.light}`,
                  p: { xs: 2, md: 3 },
                  py: { xs: 2, md: 4 },
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: { xs: 1, md: 2 },
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 8px 20px ${alpha(theme.salon.primary, 0.15)}`,
                    borderColor: theme.colors.primary[400],
                  },
                }}
              >
                <Box
                  sx={{
                    width: { xs: 70, md: 100 },
                    height: { xs: 70, md: 100 },
                    borderRadius: '50%',
                    backgroundColor: theme.colors.primary[100],
                    display: 'grid',
                    placeItems: 'center',
                    flexShrink: 0,
                    overflow: 'hidden',
                    border: `3px solid ${theme.colors.primary[300]}`,
                    boxShadow: `0 4px 12px ${alpha(theme.salon.primary, 0.2)}`,
                  }}
                >
                  {member.photo ? (
                    <Box
                      component="img"
                      src={member.photo}
                      alt={member.name}
                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <Typography
                      variant="h4"
                      sx={{ fontWeight: 600, color: theme.colors.primary[600], fontSize: '2rem' }}
                    >
                      {initials}
                    </Typography>
                  )}
                </Box>
                <Typography
                  component="h3"
                  variant="h6"
                  sx={{ fontWeight: 600, color: theme.colors.gray[700], fontSize: '1.1rem', textAlign: 'center' }}
                >
                  {member.name || t('landing_salon_details.team_member')}
                </Typography>
                <SalonPublicButton
                  preserveLegacyStyle
                  variant="contained"
                  onClick={() => onSelectMember?.(member.raw)}
                  disabled={preview}
                  aria-disabled={String(preview)}
                  fullWidth
                  sx={{
                    background: `linear-gradient(135deg, ${theme.colors.primary[500]} 0%, ${theme.colors.primary[600]} 100%)`,
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    py: 1.2,
                    borderRadius: 2,
                    textTransform: 'none',
                    boxShadow: `0 4px 12px ${alpha(theme.salon.primary, 0.3)}`,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${theme.colors.primary[600]} 0%, ${theme.colors.primary[700]} 100%)`,
                      boxShadow: `0 6px 16px ${alpha(theme.salon.primary, 0.4)}`,
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  {t('landing_salon_details.view_services')}
                </SalonPublicButton>
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </SalonPublicStyleScope>
  );
};

export default SalonTemplateTeam;
